/* Babygami product buy box: kit choice, color swatches, add to cart and the sticky bar. */
// bg-cart.js (loaded once in <head>) exposes the cart helpers; don't import it again or its click handler runs twice.
const addVariants = (...args) => window.BabygamiCart.addVariants(...args);

const SWATCH = { yellow: 'bg-swatch--yellow', teal: 'bg-swatch--teal' };

function init(root) {
  if (root.dataset.bgReady) return;
  root.dataset.bgReady = '1';

  const money = (cents) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: root.dataset.currency || 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  const threshold = Number(root.dataset.threshold || 5000);
  const freeHere = (root.dataset.country || '').toUpperCase() === (root.dataset.freeCountry || 'US').toUpperCase();
  let kits = [...root.querySelectorAll('[data-bg-kit]')];
  const fallback = root.querySelector('[data-bg-kit-current]');
  const currentId = root.dataset.currentProduct;
  const realKits = kits.filter((k) => k !== fallback);
  if (fallback && realKits.some((k) => k.querySelector('input').value === currentId)) {
    fallback.remove();
    kits = realKits;
  } else if (fallback) {
    // This product isn't one of the kit options (for example the Accessory Pack): sell just this product.
    realKits.forEach((k) => k.remove());
    fallback.hidden = false;
    fallback.querySelector('input').checked = true;
    const fs = fallback.closest('fieldset');
    if (fs) fs.hidden = true;
    kits = [fallback];
  }
  const colorsBox = root.querySelector('[data-bg-colors]');
  const colorLabel = root.querySelector('[data-bg-color-label]');
  const priceEl = root.querySelector('[data-bg-price]');
  const compareEl = root.querySelector('[data-bg-compare]');
  const saveEl = root.querySelector('[data-bg-save]');
  const atc = root.querySelector('[data-bg-atc]');
  const atcPrice = root.querySelector('[data-bg-atc-price]');
  const shipNote = root.querySelector('[data-bg-ship-note]');
  const sticky = root.querySelector('[data-bg-sticky]');
  if (!kits.length) return;

  // The gallery's first photo follows the chosen kit and color, and multi-packs get a ×2 badge.
  // Every copy of that photo (slideshow, grid, zoom dialog) is swapped; the page's own photo keeps its original markup.
  const gallery = root.closest('.shopify-section')?.querySelector('media-gallery');
  const fileOf = (url) => new URL(url, location.href).pathname;
  const sized = (url, w) => url.replace(/([?&]width=)\d+/, `$1${w}`); // kit images come from image_url, which always sets width
  const leadImg = gallery?.querySelector('.product-media img');
  const leadFile = leadImg ? fileOf(leadImg.src) : '';
  const leads = leadImg
    ? [...gallery.querySelectorAll('img')].filter((img) => fileOf(img.src) === leadFile)
        .map((img) => ({ img, src: img.src, srcset: img.srcset, alt: img.alt, max: img.dataset.maxResolution }))
    : [];
  const badges = leads
    .filter(({ img }) => img.closest('slideshow-slide, .media-gallery__grid > li'))
    .map(({ img }) => {
      const badge = document.createElement('span');
      badge.className = 'bg-qty-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.hidden = true;
      img.closest('.product-media').appendChild(badge);
      return badge;
    });

  function showInGallery(v) {
    if (!leads.length || !v.image) return;
    const own = fileOf(v.image) === leadFile;
    const qty = qtyOf(state.kit);
    leads.forEach(({ img, src, srcset, alt, max }) => {
      img.srcset = own ? srcset : [640, 1200, 1600, 1920].map((w) => `${sized(v.image, w)} ${w}w`).join(', ');
      img.src = own ? src : sized(v.image, 1600);
      img.alt = own && qty < 2 ? alt : `${state.kit.dataset.title}, ${state.color}`;
      if (max) img.dataset.maxResolution = own ? max : sized(v.image, 3840);
    });
    badges.forEach((b) => { b.hidden = qty < 2; b.textContent = `×${qty}`; });
  }

  // On phones the gallery is a slideshow: go back to the first photo so the change is visible.
  const toFirstPhoto = () => {
    const slideshow = gallery?.querySelector('slideshow-component');
    if (slideshow?.current) slideshow.select(0, undefined, { animate: false });
  };

  // Pre-select the kit that matches this product page (inside a kit option, Shopify's closest.product is the option's own product, so this is done here).
  // A link can ask for a multi-pack of it: ?kit=2 opens on Two Starter Packs (the two-pack Meta ad uses this).
  const mine = kits.filter((k) => k.querySelector('input').value === currentId);
  const wantQty = Number(new URLSearchParams(location.search).get('kit')) || 1;
  const state = { kit: mine.find((k) => Number(k.dataset.quantity || 1) === wantQty) || mine[0] || kits[0], color: root.dataset.startColor || '' };
  state.kit.querySelector('input').checked = true;

  const variantsOf = (kit) => JSON.parse(kit.dataset.variants || '[]');
  const pick = () => {
    const vs = variantsOf(state.kit);
    return vs.find((v) => v.color === state.color && v.available) || vs.find((v) => v.color === state.color) || vs.find((v) => v.available) || vs[0];
  };

  function renderColors() {
    const vs = variantsOf(state.kit);
    const colors = [...new Set(vs.map((v) => v.color))];
    if (!colors.includes(state.color)) state.color = colors[0];
    colorsBox.querySelectorAll('.bg-color').forEach((n) => n.remove());
    colorsBox.hidden = colors.length < 2 && /default/i.test(colors[0] || '');
    colors.forEach((c) => {
      const key = String(c).toLowerCase();
      const label = document.createElement('label');
      label.className = 'bg-color';
      label.innerHTML = `<input type="radio" name="${root.id || 'bg'}-color" value="${c}"><span class="bg-swatch ${SWATCH[key] || ''}" style="${SWATCH[key] ? '' : 'background:#ddd'}"></span><span class="bg-visually-hidden">${c}</span>`;
      const input = label.querySelector('input');
      input.checked = c === state.color;
      input.disabled = !vs.some((v) => v.color === c && v.available);
      input.addEventListener('change', () => { state.color = c; update(); toFirstPhoto(); });
      colorsBox.appendChild(label);
    });
  }

  // A kit can add more than one of a product (Two Starter Packs) and can show a bundle price set in the editor.
  const qtyOf = (kit) => Number(kit.dataset.quantity || 1);
  const totalOf = (kit, v) => Number(kit.dataset.price || 0) || v.price * qtyOf(kit);
  const compareOf = (kit, v) => {
    const total = totalOf(kit, v);
    const own = v.compare > v.price ? v.compare * qtyOf(kit) : 0;
    const kitCompare = Number(kit.dataset.compareTotal || 0);
    return own > total ? own : kitCompare > total ? kitCompare : 0;
  };

  function update() {
    const v = pick();
    if (!v) return;
    const total = totalOf(state.kit, v);
    const compare = compareOf(state.kit, v);
    priceEl.textContent = money(total);
    compareEl.hidden = !compare;
    saveEl.hidden = !compare;
    if (compare) { compareEl.textContent = money(compare); saveEl.textContent = `You save ${money(compare - total)}`; }
    colorLabel.textContent = state.color;
    atc.dataset.variantId = v.id;
    atc.disabled = !v.available;
    atc.firstElementChild.textContent = v.available ? 'Add to cart' : 'Sold out';
    atcPrice.textContent = money(total);
    shipNote.textContent = freeHere
      ? (total >= threshold ? 'This kit ships free in the US.' : `Free US shipping on orders over ${money(threshold)}.`)
      : 'Shipping is calculated at checkout.';
    // keep the swatch state in sync
    colorsBox.querySelectorAll('input').forEach((i) => { i.checked = i.value === state.color; });
    // sticky bar
    if (sticky) {
      sticky.querySelector('[data-bg-sticky-img]').src = v.image || '';
      sticky.querySelector('[data-bg-sticky-name]').textContent = state.kit.dataset.title;
      sticky.querySelector('[data-bg-sticky-meta]').textContent = `${state.color}, ${money(total)}`;
    }
    showInGallery(v);
    // reflect the choice in each kit's displayed price for this color
    kits.forEach((k) => {
      const kv = variantsOf(k).find((x) => x.color === state.color) || variantsOf(k)[0];
      const priceSpan = k.querySelector('[data-bg-kit-price]');
      if (!kv || !priceSpan) return;
      const kComp = compareOf(k, kv);
      priceSpan.innerHTML = money(totalOf(k, kv)) + (kComp ? ` <s>${money(kComp)}</s>` : '');
    });
  }

  kits.forEach((k) => k.querySelector('input').addEventListener('change', () => { state.kit = k; renderColors(); update(); toFirstPhoto(); }));

  async function add(btn) {
    const id = Number(atc.dataset.variantId);
    if (!id) return;
    btn.setAttribute('aria-busy', 'true');
    try { await addVariants(btn, [{ id, quantity: qtyOf(state.kit) }]); } finally { btn.removeAttribute('aria-busy'); }
  }
  atc.addEventListener('click', () => add(atc));
  if (sticky) sticky.querySelector('[data-bg-sticky-atc]').addEventListener('click', (e) => add(e.currentTarget));

  // Sticky bar: desktop shows it once the main button has scrolled away; on phones whenever the button is off screen.
  if (sticky && 'IntersectionObserver' in window) {
    document.body.appendChild(sticky); // escape any transformed/sticky ancestors so position:fixed works
    const small = window.matchMedia('(max-width: 749px)');
    const anchor = root.querySelector('[data-bg-atc-anchor]');
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    let gone = false, footerIn = false;
    const sync = () => {
      const show = gone && !footerIn;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelectorAll('[data-bg-sticky-atc], .bg-sticky-rating').forEach((el) => { el.tabIndex = show ? 0 : -1; });
    };
    new IntersectionObserver(([e]) => { gone = !e.isIntersecting && (e.boundingClientRect.top < 0 || small.matches); sync(); }).observe(anchor);
    if (footer) new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; sync(); }).observe(footer);
  }

  renderColors();
  update();
}

const boot = () => document.querySelectorAll('[data-bg-kit-picker]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot);
